import { authenticatedFetch } from '@beholos/holoscommon';
import { API_BASE_URL } from './constants';

export type FamilyMember = {
  id: string;
  email: string;
  firstname: string;
  lastname?: string;
  role: string;
  invitedAt?: Date;
  acceptedAt?: Date;
  passesRemaining: number;
  plan?: string;
  status: 'accepted' | 'pending';
};

export type FamilyListResponse = {
  familyMembers: FamilyMember[];
  maxAllowed?: number;
};

type RawFamilyMember = Record<string, any>;

const generateFallbackId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `family-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const normalizeMember = (raw: RawFamilyMember): FamilyMember => {
  const convertToDate = (value: any): Date | undefined => {
    if (!value) return undefined;
    return value instanceof Date ? value : new Date(value);
  };

  return {
    id: raw.id ?? raw._id ?? raw.userId ?? raw.inviteId ?? generateFallbackId(),
    email: raw.email ?? '',
    firstname: raw.firstname ?? raw.firstName ?? '',
    lastname: raw.lastname ?? raw.lastName,
    role: raw.role ?? '',
    invitedAt: convertToDate(raw.invitedAt ?? raw.createdAt),
    acceptedAt: convertToDate(raw.acceptedAt),
    passesRemaining: raw.passesRemaining ?? 0,
    plan: raw.plan,
    status: raw.status === 'accepted' ? 'accepted' : 'pending',
  };
};

export const getFamilyMembers = async (): Promise<FamilyListResponse> => {
  const response = await authenticatedFetch(`${API_BASE_URL}/family/list`, {
    method: 'GET',
  });

  if (!response.ok) {
    const error: any = new Error(`No se pudieron obtener los miembros. Código: ${response.status}`);
    error.status = response.status;
    throw error;
  }

  const data: any = await response.json();
  const rawMembers = Array.isArray(data) ? data : data.familyMembers ?? data.members ?? [];

  return {
    familyMembers: rawMembers.map(normalizeMember),
    maxAllowed: data.maxAllowed ?? data.limit ?? data.maxMembers,
  };
};

export const sendFamilyInvite = async (email: string): Promise<string> => {
  const response = await authenticatedFetch(`${API_BASE_URL}/family/invite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `No se pudo enviar la invitación. Código: ${response.status}`);
  }

  const data = await response.json().catch(() => ({}));
  return data.message ?? 'Tu familiar, al aceptar, tendrá:';
};
